/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Autocomplete,
  TextField,
  Grid,
  Typography,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { notificationActions } from 'modules/common/notifications/slice';
import CustomToggleSwitch from 'modules/common/components/toggle-switch';
import { useEffect, useRef, useState } from 'react';
import GetAppIcon from '@mui/icons-material/GetApp';
import SaveFilter from 'modules/common/components/add-hotel-group/components/save-filter';
import LoadFilter from 'modules/common/components/add-hotel-group/components/load-filter';
import AggregationTransferList from 'modules/common/components/add-hotel-group/components/add-hotel-group-transfer-list';
import QueryBox from 'modules/common/components/add-hotel-group/components/query-box';
import { dashboardActions } from 'modules/dashboard/slice';
import { X } from 'react-feather';
import {
  selectAdvancedQueryErrorMessage,
  selectHotelGroupName,
  selectHotelGroupQuery,
  selectHotelGroups,
  selectIsDemoModeActivated,
} from 'modules/dashboard/selectors';
import { selectConsent, selectOpenAi } from 'modules/users/selectors';
import { ENVIRONMENT } from 'config';
import Modal from 'modules/common/components/modal';
import { usersActions } from 'modules/users/slice';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import LLMInputBox from '../llm-input-box';
import ImportHotelSetButton from '../import-hotel-set/import-hotel-set-button';
import { isExplicitQuery } from '../../functions';
import { isEmpty } from '../../../../helpers/object';
import { DATE_TIME_PATTERN, DATE_TIME_WITH_AM_PM_PATTERN } from '../../../../constants/date-range';
import { formatDate } from '../../../../../dashboard/functions';

/**
 * Custom aggregation filter component is implemented
 * @param {Boolean} open - To open/ close dialog box
 * @param {Function} onClose - Function to trigger on close event
 * @returns
 */
const AddHotelGroup = ({
  open,
  onClose,
  hotelList,
  selectedHotelGroupName,
  enableHotelGroups,
  isEditable = false,
  isPrimary,
}) => {
  const dispatch = useDispatch();
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const query = useSelector(selectHotelGroupQuery);
  const selectedGroupName = useSelector(selectHotelGroupName);
  const advancedQueryErrorMessage = useSelector(selectAdvancedQueryErrorMessage);
  const advancedQueryInterpreter = useSelector(selectOpenAi);
  const consent = useSelector(selectConsent);
  const organizationId = useSelector(selectOrganizationId);
  const currentUser = useSelector(selectAuthUser);
  const filters = useSelector(selectHotelGroups);
  //
  const [groupHotelList, setGroupHotelList] = useState([]);
  const [showLoadFilter, setShowLoadFilter] = useState(false);
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [loadFilter, setLoadFilter] = useState(false);
  const [temGroupList, setTemGroupList] = useState([]);
  const [isExportDisabled, setIsExportDisabled] = useState(true);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isAllClear, setAllClear] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const [filterErrorMessage, setFilterErrorMessage] = useState(
    'Unable to process your request. Please check your input and try again.'
  );
  const [isQueryEnabledErrorMessage, setIsQueryEnabledErrorMessage] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  //
  useEffect(() => {
    if (!advancedQueryInterpreter) return;

    const isInputTokenLimitReached =
      advancedQueryInterpreter.inputTokenCount + 1000 > ENVIRONMENT.GPT_INPUT_TOKEN_THRESHOLD;
    const isOutputTokenLimitReached =
      advancedQueryInterpreter.outputTokenCount > ENVIRONMENT.GPT_OUTPUT_TOKEN_THRESHOLD;
    let message;
    if (isInputTokenLimitReached && isOutputTokenLimitReached) {
      message = 'You have no token credits remaining. Please try again later or contact support.';
    } else if (isInputTokenLimitReached) {
      message =
        'You have no input token credits remaining. Please try again later or contact support.';
    } else if (isOutputTokenLimitReached) {
      message =
        'You have no output token credits remaining. Please try again later or contact support.';
    }
    //
    if (!message) return;

    setIsQueryEnabledErrorMessage(message);
  }, [advancedQueryInterpreter, isQueryEnabled]);
  //
  useEffect(() => {
    if (isFocus && consent?.isFirstTime) {
      setIsFirstTime(true);
    } else {
      setIsFirstTime(false);
    }
  }, [consent, isFocus]);
  //
  const previewButtonRef = useRef(null);
  //
  const [loadList, setLoadList] = useState([]);
  useEffect(() => {
    if (advancedQueryErrorMessage) {
      setIsQueryEnabledErrorMessage(advancedQueryErrorMessage);
    }
  }, [advancedQueryErrorMessage]);
  //
  useEffect(() => {
    if (filters?.results?.length > 0) {
      const list = filters?.results?.map((a) => ({
        id: a?.hotelGroups?.hotelGroupName,
        label: a?.hotelGroups?.hotelGroupName,
        query: a?.hotelGroups?.hotelQuery,
        description: a.hotelGroups.description,
      }));

      setLoadList(list);
    }
  }, [filters]);
  //
  useEffect(() => {
    if (enableHotelGroups && isEditable && loadList?.length > 0 && open) {
      const selectedGroup = loadList?.find((a) => a.id === selectedHotelGroupName?.id);
      setLoadFilter(true);
      setSelectedValue(selectedGroup);
      dispatch(dashboardActions.setHotelGroupQuery(selectedGroup?.query?.trim()));
      dispatch(dashboardActions.setSelectedHotelGroupName(selectedGroup?.label));
      dispatch(dashboardActions.setSelectedHotelGroupDescription(selectedGroup?.description));
    }
  }, [isEditable, loadList, selectedHotelGroupName, open, enableHotelGroups]);
  // Triggered once when the component mounted
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);
  //
  useEffect(() => {
    setIsExportDisabled(isEmpty(temGroupList));
  }, [temGroupList]);
  //
  useEffect(() => {
    // Update global and local states
    if (open && (!isEditable || isAllClear)) {
      setGroupHotelList([]);
      setTemGroupList([]);
      setSelectedValue(null);
      dispatch(dashboardActions.setHotelGroupQuery(''));
      dispatch(dashboardActions.setRestLLMRequestState(false));
      dispatch(dashboardActions.setNaturalLanguageQuery(''));
      dispatch(dashboardActions.setSelectedHotelGroupName(''));
      dispatch(dashboardActions.setSelectedHotelGroupDescription(''));
      setAllClear(false);
    }
  }, [open, isEditable, isAllClear]);
  //
  const modalAction = (reason, action) => {
    if (reason && reason === 'backdropClick') return;
    setSelectedValue(null);
    onClose(action);
  };
  //
  const resetForm = () => {
    setGroupHotelList([]);
    setTemGroupList([]);
    setSelectedValue(null);
    dispatch(dashboardActions.setHotelGroupQuery(''));
    dispatch(dashboardActions.setRestLLMRequestState(false));
    dispatch(dashboardActions.setNaturalLanguageQuery(''));
    dispatch(dashboardActions.setSelectedHotelGroupName(''));
    dispatch(dashboardActions.setSelectedHotelGroupDescription(''));
  };
  //
  const encodeBase64 = (str) => btoa(str);
  //
  const createExplicitHotelSet = () => {
    const id = temGroupList?.map((item) => item.id);
    //
    return encodeBase64(
      JSON.stringify({
        type: 'user_defined_hotel_set',
        isExplicit: true,
        explicit_list: id,
      })
    );
  };
  //
  const createQueryHotelSet = (q) =>
    encodeBase64(
      JSON.stringify({
        type: 'user_defined_hotel_set',
        isExplicit: false,
        query: q,
      })
    );

  /**
   * Download a string as a JSON file.
   * @param {string} content - The string content to save in the file.
   * @param {string} [filename='hotel_set.json'] - The name of the downloaded file.
   */
  const downloadStringAsJSON = (content) => {
    const baseName = selectedGroupName ?? 'Hotel_Set';
    const fileName = `${baseName} ${formatDate(new Date(), DATE_TIME_WITH_AM_PM_PATTERN)}.json`;
    //
    const downloadLink = document.createElement('a');
    downloadLink.href = `data:application/json;charset=utf-8,${encodeURIComponent(content)}`;
    downloadLink.download = fileName;
    downloadLink.click();
  };
  //
  const exportHotelSet = () => {
    const isExplicit = isExplicitQuery(query);
    let hotelSetObj;
    if (isExplicit) {
      hotelSetObj = createExplicitHotelSet();
    } else {
      hotelSetObj = createQueryHotelSet(query);
    }
    downloadStringAsJSON(hotelSetObj);
  };
  //
  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => modalAction(reason, false)}
        aria-labelledby="form-dialog-title"
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '800px',
            },
          },
        }}
      >
        <DialogTitle sx={{ paddingBottom: 2 }}>
          <Grid container item flexDirection="row" justifyContent="space-between" xs={12}>
            <Grid item xs={3.5}>
              <Typography variant="h5">Add/Edit Hotel Set </Typography>
            </Grid>
            <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                sx={{ minWidth: 250, mr: 35 }}
                value={selectedValue}
                options={loadList ?? []}
                getOptionLabel={(option) => (option.label ? option.label : '')}
                isOptionEqualToValue={(option, value) => value !== null && option?.id === value?.id}
                filterSelectedOptions
                onChange={(event, value) => {
                  setLoadFilter(true);
                  setSelectedValue(value);
                  dispatch(dashboardActions.setHotelGroupQuery(value?.query));
                  dispatch(dashboardActions.setSelectedHotelGroupName(value?.label));
                  dispatch(dashboardActions.setSelectedHotelGroupDescription(value?.description));
                }}
                disableClearable
                disabled={loadList?.length < 1}
                renderInput={(params) => (
                  <TextField {...params} label="Hotel Sets" size="small" color="primary" />
                )}
              />
              <Grid mr={4}>
                <Tooltip title="Export Hotel Sets">
                  <IconButton
                    size="small"
                    onClick={exportHotelSet}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        width: '1.25rem',
                        height: '1.25rem',
                      },
                    }}
                    disabled={isExportDisabled}
                  >
                    <GetAppIcon sx={{ fontSize: 20, cursor: 'pointer' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
              <ImportHotelSetButton
                previewButtonRef={previewButtonRef}
                setIsQueryEnabled={setIsQueryEnabled}
                hotelList={hotelList}
                setFilterErrorMessage={setFilterErrorMessage}
                setFilterError={setFilterError}
              />
            </Grid>
            <Grid item xs={3.5}>
              <IconButton
                size="small"
                aria-label="close"
                onClick={() => {
                  setAllClear(false);
                  modalAction(false);
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <X />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              borderRadius: '8px',
              borderColor: '#8c8787 !important',
              border: 1,
              padding: 4,
            }}
          >
            <AggregationTransferList
              focusOnList={hotelList}
              groupList={groupHotelList}
              setGroupList={(value) => setGroupHotelList(value)}
              isQueryEnabled={isQueryEnabled}
              temGroupList={temGroupList}
              setTemGroupList={setTemGroupList}
            />
          </Box>
          {isDemoModeActivated && (
            <Box
              sx={{
                marginTop: 8,
              }}
            />
          )}
          <Box
            sx={{
              borderRadius: '8px',
              borderColor: '#8c8787 !important',
              border: 1,
              padding: 2,
              marginBottom: 4,
              marginTop: 4,
              display: isDemoModeActivated ? 'none' : 'block',
            }}
          >
            <CustomToggleSwitch
              label="Advanced Querying"
              title=""
              onChange={(event) => setIsQueryEnabled(event.target.checked)}
              checked={isQueryEnabled}
              paddingBottom={3}
            />
            <LLMInputBox
              open={open}
              isFirstTime={consent?.isFirstTime}
              setIsFocus={setIsFocus}
              setFilterError={setFilterError}
            />
            <QueryBox
              setGroupList={setGroupHotelList}
              onClose={() => {
                modalAction(false);
              }}
              open={open}
              isQueryEnabled={isQueryEnabled}
              loadFilter={loadFilter}
              setLoadFilter={setLoadFilter}
              focusOnList={hotelList}
              setTemGroupList={setTemGroupList}
              setFilterError={setFilterError}
              filterErrorMessage={filterErrorMessage}
              isQueryEnabledErrorMessage={isQueryEnabledErrorMessage}
              previewButtonRef={previewButtonRef}
              filterError={filterError}
            />
          </Box>
          <SaveFilter
            groupList={groupHotelList}
            hotelList={hotelList}
            setGroupList={setGroupHotelList}
            setTemGroupList={setTemGroupList}
            onClose={() => {
              modalAction(false);
              setAllClear(false);
            }}
            setAllClear={setAllClear}
            resetForm={resetForm}
            isPrimary={isPrimary}
            filterError={filterError}
            setFilterError={setFilterError}
          />
          <LoadFilter
            openLoad={showLoadFilter}
            onClose={() => setShowLoadFilter(false)}
            setLoadFilter={setLoadFilter}
          />
        </DialogContent>
      </Dialog>
      <Modal
        open={isFirstTime}
        handleClose={() => {
          setIsFirstTime(false, setIsFocus(false));
        }}
        title="AI-Powered Features"
        content="This platform offers functionality that leverages generative AI to support data interpretation and other operations.
Please note that AI-generated results may not always be accurate or complete; we recommend reviewing the results before using them for analysis or decision-making.
By clicking “Accept”, you acknowledge and agree to these considerations in the use of such features on this platform."
        handleSuccess={() =>
          dispatch(
            usersActions.updateUserConfiguration({
              organizationId,
              userId: currentUser?.id,
              consent: {
                isFirstTime: false,
                updatedTime: formatDate(new Date(), DATE_TIME_PATTERN)
              },
            }),
            setIsQueryEnabled(true)
          )
        }
        closeLabel="Cancel"
        successLabel="Accept"
        variant="contained"
        color="primary"
      />
    </>
  );
};
//
export default AddHotelGroup;
